import React, { useState } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CardMedia
} from '@mui/material';
import LoadingOverlay from '../common/LoadingOverlay';
import { Add, Delete, Folder } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { ProjectsListQuery, SAVE_PROJECT, DELETE_PROJECT } from '../../graphql/projects';
import ProjectModal from './ProjectModal';
import getApiUrl from '../../utils/apiUrl';

const Projects = () => {
  const navigate = useNavigate();
  const { loading, error, data, refetch } = useQuery(ProjectsListQuery, {
    variables: { page: 1, size: 1000 },
    fetchPolicy: 'cache-and-network'
  });
  const [saveProject] = useMutation(SAVE_PROJECT);
  const [deleteProject] = useMutation(DELETE_PROJECT);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  if (loading) return <LoadingOverlay />;
  if (refreshing) return <LoadingOverlay />;
  if (error) return <Typography color="error">Error: {error.message}</Typography>;

  const projects = data?.projects?.list || [];
  const companies = data?.companies?.list || [];

  const getCompanyNames = (companyIds) => {
    if (!companyIds || companyIds.length === 0) return '';
    return companyIds
      .map(id => companies.find(c => c.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    let date;
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [day, month, year] = dateString.split('-');
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleDelete = async () => {
    try {
      await deleteProject({ variables: { id: projectToDelete.id } });
      refetch();
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      // Handle error silently
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Folder sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Projects
          </Typography>
        </Box>
        <Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<Add />}
          onClick={() => {
            setSelectedProject(null);
            setModalOpen(true);
          }}
        >
          Add New Project
        </Button>
        </Box>
      </Box>
      <Grid container spacing={3}>
        {projects.map((project) => (
          <Grid item xs={12} md={6} key={project.id}>
            <Card
              sx={{
                display: 'flex',
                height: 300,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
              onClick={() => navigate(`/projects/${project.id}`, { state: { project } })}
            >
              <Box sx={{ width: '50%', p: '10px', pl: 2 }}>
                {project.projectImage?.url ? (
                  <CardMedia
                    component="img"
                    sx={{ 
                      width: '100%',
                      height: 'calc(100% - 20px)',
                      objectFit: 'cover',
                      borderRadius: 1
                    }}
                    image={getApiUrl(project.projectImage.url)}
                    alt={project.name}
                  />
                ) : (
                  <Box sx={{ 
                    width: '100%',
                    height: 'calc(100% - 20px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1
                  }}>
                    <img 
                      src="/dark_logo_transparent.png" 
                      alt="Weldingo" 
                      style={{ width: '120px', height: 'auto', opacity: 0.2 }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', justifyContent: 'space-between', p: 2 }}>
              <CardContent sx={{ p: 0, pb: 1 }}>
                <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 600 }}>
                  {project.name}{project.number ? ` (${project.number})` : ''}
                </Typography>
                {project.customer && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                    Customer: <strong>{project.customer}</strong>
                  </Typography>
                )}
                {getCompanyNames(project.company_ids) && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                    Companies: <strong>{getCompanyNames(project.company_ids)}</strong>
                  </Typography>
                )}
                {(project.planned_start_date || project.planned_end_date) && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                    Dates: <strong>{formatDate(project.planned_start_date) || 'TBD'}</strong> - <strong>{formatDate(project.planned_end_date) || 'TBD'}</strong>
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ p: 0, justifyContent: 'flex-end', minHeight: 'auto' }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="error" 
                  startIcon={<Delete />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setProjectToDelete(project);
                    setDeleteDialogOpen(true);
                  }}
                >
                  Delete
                </Button>
              </CardActions>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      <ProjectModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        companies={companies}
        onSave={async (formData) => {
          try {
            const attributes = { ...formData };
            if (selectedProject) {
              attributes.id = selectedProject.id;
            }
            const result = await saveProject({ variables: { attributes } });
            setTimeout(() => {
              refetch({ fetchPolicy: 'network-only' });
            }, 2000);
            return result.data.createOrUpdateProjects.project;
          } catch (error) {
            throw error;
          }
        }}
        onUploadComplete={async () => {
          setRefreshing(true);
          await refetch({ fetchPolicy: 'network-only' });
          setRefreshing(false);
        }}
      />
      
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete project "{projectToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Projects;
